import { useQuery, useQueries, useQueryClient } from 'react-query';
import { Options } from '@frontend/fetch';
import { LimitedSchemaQueryOptions, LimitedSchemaFetchQueryOptions } from '@frontend/react-query-helpers';
import { SchemaSMSDataV4Service } from '../service';
import { GetUnifiedThreadItemIO } from '../types';

export const getGetUnifiedThreadItemQueryKey = (req: GetUnifiedThreadItemIO['input']) =>
  ['V4', 'GetUnifiedThreadItem', req] as const;
type QueryKey = ReturnType<typeof getGetUnifiedThreadItemQueryKey>;

/**
 * A hook that returns a query for the `GetUnifiedThreadItem` endpoint.
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useGetUnifiedThreadItemQuery = <E = unknown, D = GetUnifiedThreadItemIO['output']>(
  req: GetUnifiedThreadItemIO['input'],
  options?: LimitedSchemaQueryOptions<GetUnifiedThreadItemIO, E, D, QueryKey>,
  httpOptions?: Options
) =>
  useQuery<GetUnifiedThreadItemIO['output'], E, D, QueryKey>({
    queryKey: getGetUnifiedThreadItemQueryKey(req),
    queryFn: () => SchemaSMSDataV4Service.GetUnifiedThreadItem(req, httpOptions),
    ...options,
  });

/**
 * A hook that returns an array of queries for the `GetUnifiedThreadItem` endpoint.
 * It takes an array of objects with the following properties:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useGetUnifiedThreadItemQueries = <E = unknown, D = GetUnifiedThreadItemIO['output']>(
  argsArr: {
    req: GetUnifiedThreadItemIO['input'];
    options?: LimitedSchemaQueryOptions<GetUnifiedThreadItemIO, E, D, QueryKey>;
    httpOptions?: Options;
  }[]
) =>
  useQueries(
    argsArr.map(({ req, options, httpOptions }) => ({
      queryKey: getGetUnifiedThreadItemQueryKey(req),
      queryFn: () => SchemaSMSDataV4Service.GetUnifiedThreadItem(req, httpOptions),
      ...options,
    }))
  ) as ReturnType<typeof useGetUnifiedThreadItemQuery<E, D>>[];

/**
 * A hook that returns a function to fetch the query for the `GetUnifiedThreadItem` endpoint.
 * It uses `queryClient.fetchQuery` to ensure proper caching behavior.
 * NOTE: this should only be used in specific scenarios where you need to manually fetch the query. Otherwise,
 * `useQuery` should be used. Manual error and loading states need to be handled when using this function.
 *
 * @returns The function to fetch the query, with the following args:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `fetchQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useFetchGetUnifiedThreadItem = () => {
  const queryClient = useQueryClient();

  const fetchGetUnifiedThreadItem = async <E = unknown, D = GetUnifiedThreadItemIO['output']>(
    req: GetUnifiedThreadItemIO['input'],
    options?: LimitedSchemaFetchQueryOptions<GetUnifiedThreadItemIO, E, D, QueryKey>,
    httpOptions?: Options
  ) => {
    const response = await queryClient.fetchQuery<GetUnifiedThreadItemIO['output'], E, D, QueryKey>(
      getGetUnifiedThreadItemQueryKey(req),
      () => SchemaSMSDataV4Service.GetUnifiedThreadItem(req, httpOptions),
      options
    );

    return response;
  };

  return fetchGetUnifiedThreadItem;
};
